/* =====================================================================
   DIGITAL TWIN TEXT — English (en) and Korean (ko).
   Numbers live in src/twin/sampleData.js; formulas in src/twin/calc.js.
   {placeholders} are filled in by the component.
   ===================================================================== */

export const twin = {
  en: {
    mission: {
      label: "Challenge",
      steps: ["Find the bottleneck", "Move an operator", "Beat the clock"],
      tip: "Think of the line like a road: the narrowest lane sets everyone's speed.",
      start: "Which station is holding {line} back? Tap the one you think is slowest, in the drawing or in the list.",
      wrong: "Not this one. {op} can make {cap} pcs/h and spends {idle}% of its time waiting for work. Look for the shortest bar.",
      found: "Got it! {op} can only make {rate} pcs/h, so the whole line can't go faster. Now press “Move 1 operator” to give it help.",
      done: "Nice. The line now makes {after} pcs/h instead of {before}, and reaches the target at {clock} instead of {clock0}. The bottleneck moved to {next}: there is always a slowest station.",
      noHelp: "Moving people doesn't help here. {op} is limited by machine downtime, so the fix is maintenance, not balancing. Try another line.",
      reveal: "Show me",
      again: "Try another line",
      hidden: "?",
    },
    score: { rate: "Line makes", target: "Target {target} pcs reached at", bottleneck: "Bottleneck" },
    pick: "Select a station to see its details.",
    title: "Find the bottleneck. Test the fix first.",
    intro: "A playable model of three sample sewing lines. Follow the challenge: find the station that slows the line, move an operator, and watch the finish time change.",
    badge: "Simulated digital twin demonstration using sample data. Not connected to a factory.",
    line: "Line",
    scenario: "Scenario",
    reset: "Reset",
    hint: "Drag to turn the view. Tap a station to select it.",
    canvasLabel: "Simulated {line}, {n} workstations in a U-shaped line. Bottleneck: {op}. The station list below has the same information.",
    floorIn: "Bundles in",
    floorOut: "Finished",
    bottleneck: "Bottleneck",
    status: { running: "Running", idle: "Idle", stopped: "Stopped" },
    statusHelp: { running: "working at its pace", idle: "waiting for work from the bottleneck", stopped: "machine stopped now" },
    stationsTitle: "Stations on {line}",
    stationsNote: "Bars compare each station's capacity with the line rate. The shortest bar sets the pace for the whole line.",
    cols: { station: "Station", status: "Status", ops: "Operators", capacity: "Capacity", util: "Utilisation" },
    detail: {
      title: "Station {n}",
      operation: "Operation",
      operators: "Operators",
      smv: "Standard time",
      capacity: "Capacity",
      util: "Utilisation",
      completed: "Completed this shift",
      wip: "Waiting in front",
      downtime: "Downtime so far",
      isBottleneck: "This station sets the pace for the line.",
      notBottleneck: "Line rate is set by {op}.",
    },
    units: { pcsH: "pcs/h", pcs: "pcs", min: "min", minPc: "min/pc", people: "" },
    metrics: {
      title: "Before and after",
      before: "Now",
      after: "With scenario",
      change: "Change",
      rate: "Line rate",
      output: "Output so far",
      projected: "Projected at shift end",
      target: "Daily target",
      gap: "Against target",
      targetAt: "Target reached at",
      bottleneck: "Bottleneck",
      balance: "Line balance",
      overtime: "{h} h {m} min overtime",
      overtimeH: "{h} h overtime",
      overtimeM: "{m} min overtime",
      inShift: "within shift",
      never: "not reached",
    },
    moves: {
      none: "Current allocation. No operators moved.",
      did: "Moved {n} operator(s):",
      arrow: "to",
      noHelp: "No operator move raises the line rate. The limit here is {op}: reduce its downtime or add capacity there.",
      fewer: "Only {n} move helped. A further move would not raise the line rate.",
    },
    state: {
      loading: "Loading line data…",
      stale: "This data is {n} minutes old. Figures may have changed since.",
      disconnected: "The line data source can't be reached. Showing the last sample snapshot.",
      empty: "There are no lines to show yet.",
      sample: "Sample data, snapshot at {time}.",
    },
    assumptions: {
      title: "How the numbers are calculated",
      items: [
        "Capacity per station = operators × 60 ÷ standard minutes per piece (SMV) × observed performance × availability. Availability = 1 − downtime ÷ time worked.",
        "A line is modelled as one sequence of operations, so the line rate equals the lowest station capacity. That station is the bottleneck.",
        "The shift is 08:00–17:00 with a one-hour lunch at 12:00 (8 working hours). The snapshot is taken at 12:00.",
        "Projections assume the current rate holds for the rest of the shift, with no rework, absence or style change.",
        "A balancing scenario moves one operator at a time from the station whose loss hurts least to the bottleneck. It assumes the operator is trained for the new operation. Output already made does not change.",
        "Line balance = total SMV ÷ (operators × the slowest cycle time). Idle means a station is busy less than half the time because the bottleneck starves it.",
      ],
      future: "In K SMART, suggestions like these are planned as an AI-assisted feature trained on a factory's own validated history. This page uses a simple rule on sample data instead.",
    },
  },

  ko: {
    mission: {
      label: "도전 과제",
      steps: ["병목 찾기", "작업자 옮기기", "목표 시각 앞당기기"],
      tip: "라인은 도로와 같습니다. 가장 좁은 차선이 모두의 속도를 정합니다.",
      start: "{line}을 붙잡고 있는 공정은 어디일까요? 가장 느리다고 생각하는 공정을 그림이나 목록에서 눌러 보세요.",
      wrong: "이 공정은 아닙니다. {op} 공정은 시간당 {cap}장을 만들 수 있고, 시간의 {idle}%는 일감을 기다립니다. 가장 짧은 막대를 찾아보세요.",
      found: "정답입니다! {op} 공정은 시간당 {rate}장밖에 못 만들어서 라인 전체가 더 빨라질 수 없습니다. 이제 ‘작업자 1명 이동’을 눌러 도와주세요.",
      done: "좋습니다. 라인 속도가 시간당 {before}장에서 {after}장이 되었고, 목표 달성 시각이 {clock0}에서 {clock}(으)로 바뀌었습니다. 병목은 {next} 공정으로 옮겨졌습니다. 가장 느린 공정은 항상 있기 마련입니다.",
      noHelp: "여기서는 작업자를 옮겨도 도움이 되지 않습니다. {op} 공정은 설비 정지 때문에 느려서 밸런싱이 아니라 정비가 해법입니다. 다른 라인을 선택해 보세요.",
      reveal: "정답 보기",
      again: "다른 라인 해 보기",
      hidden: "?",
    },
    score: { rate: "라인 생산 속도", target: "목표 {target}장 달성 시각", bottleneck: "병목" },
    pick: "공정을 선택하면 자세한 내용을 볼 수 있습니다.",
    title: "병목을 찾고, 바꾸기\u00A0전에 먼저 시험해 봅니다.", // \u00A0 keeps "바꾸기 전에" on one line
    intro: "샘플 봉제 라인 세 개를 직접 다뤄 볼 수 있는 모델입니다. 도전 과제를 따라 라인을 늦추는 공정을 찾고, 작업자를 옮겨 완료 시각이 어떻게 바뀌는지 확인하세요.",
    badge: "샘플 데이터를 사용한 시뮬레이션 디지털 트윈 데모입니다. 실제 공장과 연결되어 있지 않습니다.",
    line: "라인",
    scenario: "시나리오",
    reset: "초기화",
    hint: "드래그해서 시점을 돌리고, 공정을 눌러 선택하세요.",
    canvasLabel: "시뮬레이션 {line}, U자형 라인의 공정 {n}개. 병목: {op}. 아래 공정 목록에 같은 정보가 있습니다.",
    floorIn: "번들 투입",
    floorOut: "완성",
    bottleneck: "병목",
    status: { running: "가동", idle: "대기", stopped: "정지" },
    statusHelp: { running: "정상 속도로 작업 중", idle: "병목 때문에 작업을 기다리는 중", stopped: "현재 설비 정지" },
    stationsTitle: "{line} 공정",
    stationsNote: "막대는 각 공정의 생산 능력을 라인 속도와 비교합니다. 가장 짧은 막대가 라인 전체의 속도를 정합니다.",
    cols: { station: "공정", status: "상태", ops: "작업자", capacity: "생산 능력", util: "가동률" },
    detail: {
      title: "{n}번 공정",
      operation: "작업",
      operators: "작업자",
      smv: "표준 시간",
      capacity: "생산 능력",
      util: "가동률",
      completed: "이번 근무 완료",
      wip: "앞에서 대기 중",
      downtime: "누적 정지 시간",
      isBottleneck: "이 공정이 라인 전체의 속도를 정합니다.",
      notBottleneck: "라인 속도는 {op} 공정이 정합니다.",
    },
    units: { pcsH: "장/시간", pcs: "장", min: "분", minPc: "분/장", people: "명" },
    metrics: {
      title: "변경 전과 후",
      before: "현재",
      after: "시나리오 적용",
      change: "변화",
      rate: "라인 속도",
      output: "현재까지 생산",
      projected: "근무 종료 시 예상",
      target: "일일 목표",
      gap: "목표 대비",
      targetAt: "목표 달성 시각",
      bottleneck: "병목",
      balance: "라인 밸런스",
      overtime: "잔업 {h}시간 {m}분",
      overtimeH: "잔업 {h}시간",
      overtimeM: "잔업 {m}분",
      inShift: "근무 시간 내",
      never: "달성 불가",
    },
    moves: {
      none: "현재 배치입니다. 이동한 작업자가 없습니다.",
      did: "작업자 {n}명 이동:",
      arrow: "→",
      noHelp: "작업자를 옮겨도 라인 속도가 오르지 않습니다. 이 라인의 한계는 {op} 공정입니다. 정지 시간을 줄이거나 그 공정의 능력을 늘려야 합니다.",
      fewer: "도움이 되는 이동은 {n}건뿐입니다. 더 옮겨도 라인 속도가 오르지 않습니다.",
    },
    state: {
      loading: "라인 데이터를 불러오는 중…",
      stale: "{n}분 전 데이터입니다. 그 사이 수치가 바뀌었을 수 있습니다.",
      disconnected: "라인 데이터에 연결할 수 없습니다. 마지막 샘플 데이터를 표시합니다.",
      empty: "아직 표시할 라인이 없습니다.",
      sample: "샘플 데이터, {time} 기준.",
    },
    assumptions: {
      title: "수치 계산 방법",
      items: [
        "공정별 생산 능력 = 작업자 수 × 60 ÷ 장당 표준 시간(SMV) × 실제 작업 성과 × 가동 가능률. 가동 가능률 = 1 − 정지 시간 ÷ 작업 시간.",
        "라인은 하나의 공정 순서로 단순화했습니다. 따라서 라인 속도는 가장 낮은 공정 능력과 같고, 그 공정이 병목입니다.",
        "근무는 08:00–17:00이며 12:00에 점심 1시간이 있습니다(실작업 8시간). 스냅샷은 12:00 기준입니다.",
        "예측은 남은 근무 시간 동안 현재 속도가 유지되고 재작업, 결근, 스타일 변경이 없다고 가정합니다.",
        "밸런싱 시나리오는 빠져도 영향이 가장 적은 공정에서 병목 공정으로 작업자를 한 명씩 옮깁니다. 작업자가 새 공정에 숙련되어 있다고 가정하며, 이미 생산한 수량은 바뀌지 않습니다.",
        "라인 밸런스 = 총 SMV ÷ (작업자 수 × 가장 느린 사이클 타임). 대기는 병목 때문에 일감이 부족해 가동률이 50% 미만인 공정입니다.",
      ],
      future: "K SMART에서는 이런 제안을 공장의 검증된 실제 이력으로 학습한 AI 보조 기능으로 제공할 계획입니다. 이 페이지는 샘플 데이터에 단순한 규칙을 적용한 것입니다.",
    },
  },
};
